import { engineComponent, ArrayUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { Ball } from "./Ball";
import { Configuration, RouletteWheelConfigurationAttributes } from "../Components/Configuration";
import { AppContext } from "../../Common/AppContext";
import { GameManager } from "../Managers/GameManager";
import { GameData } from "../Channels/Common/Datas/GameData";
import { DataManager } from "../Managers/DataManager";
import { RenderTextureElement } from "../CustomComponents/RenderTextureElement";
import { ImageElement } from "../Components/Elements/Graphics/ImageElement";
import { SwitchElementExtended } from "../CustomComponents/SwitchElementExtended";
import { Communication } from "neo-games";

/**
 * Machine handle reels and symbols for reels rotation.
 * Machine define behaviour for complete flow of reel rotation.
 * @class Machine
 * @extends BaseElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class Wheel extends ContainerElement {
    // events for view when wheel stopped
    public static WheelStopped: string = "eventWheelStopped";

    // numbers in order to angle from 0 to 360
    private static NumberOrder: Array<number>;

    private _spinContainer: ContainerElement;

    private _ball: Ball;

    // min spin rotation in frames
    protected _minSpinRotation: number;
    // min spin rotation in frames for high wins
    protected _minSpinRotationLong: number;

    // timer for minimal time to stop reels
    protected _stopTimer: FrameTimerEventEmitter;

    // timer for minimal time to stop reels
    protected _wheelStopTimer: FrameTimerEventEmitter;

    // timer for defined time in configuration.json to stop reels
    protected _legalStopTimer: number;

    // boolean which indicates that machine can stop and rotate min defined time
    protected _stoppedFromMachine: boolean;
    // boolean which indicates that machine can stop and response has arrived
    protected _stoppedFromServer: boolean;
    // boolean which indicates that machine can stop and rotate min legal time
    protected _stoppedFromLegal: boolean;

    protected _startSpinTime: number;

    protected _chosenNumber: number;

    constructor() {
        super();

        this._stoppedFromServer = false;
        this._stoppedFromMachine = false;
        this._stoppedFromLegal = false;

        this._stopTimer = CreateFrameTimer("Wheel");
        this._stopTimer.on(FrameTimerEvents.Complete, this.spinStop.bind(this));

        this._wheelStopTimer = CreateFrameTimer("WheelStop");
        this._wheelStopTimer.on(FrameTimerEvents.Complete, this.emitWheelStopped.bind(this));
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    public create(node: Element): void {
        super.create(node);

        if (node.hasAttribute(WheelAttributes.CONFIGS)) {
            let configs: Array<string> = node.getAttribute(WheelAttributes.CONFIGS).split(";");
            for (let i: number = 0; i < configs.length; i++) {
                let config: Element = Configuration.Configurations[configs[i]];
                if (config != null) {
                    this.configurate(config);
                }
            }
        }

        if (this.childs != null) {
            let wheel = <SwitchElement>this.childs["Spin"];

            if (wheel) {
                this._spinContainer = <ContainerElement>wheel.getChildElementByName("SpinContainer");

                if (this._spinContainer) {
                    let ballHolder = <SwitchElementExtended>this._spinContainer.childs["BallHolder"];

                    if (ballHolder) {
                        this._ball = new Ball();
                        this._ball.createBall(ballHolder);
                        this._ball.on(Ball.BallStopped, this.wheelStopped.bind(this));
                    }
                }
            }
        }
    }

    initialize(statistics: Communication.Game.IRouletteStatistics): void {
        if (statistics == null) {
            this.setInitBallPosition(Math.floor(Math.random() * 36));
            return;
        }

        const lastWinningNumbers = statistics.lastWinningNumbers;
        if (lastWinningNumbers != null && lastWinningNumbers.length > 0) {
            this.setInitBallPosition(lastWinningNumbers[lastWinningNumbers.length - 1]);
        }
    }

    private setInitBallPosition(lastWinNumber: number) {
        if (lastWinNumber != null) {
            let initAngle = Wheel.NumberOrder.indexOf(lastWinNumber) * (360 / Wheel.NumberOrder.length);
            this._ball.initialize(initAngle);
        }
    }

    /**
     * Get attributes from configuration and set up supported properties.
     * @method configurate
     * @param node is Element from xml
     * @return void
     */
    private configurate(node: Element): void {
        /*
         * Wheel strip numbers starts with number on 0deg
         */
        if (node.hasAttribute(RouletteWheelConfigurationAttributes.WHEEL_STRIP)) {
            Wheel.NumberOrder = ArrayUtils.numberArray(
                node.getAttribute(RouletteWheelConfigurationAttributes.WHEEL_STRIP),
            );
        }

        /*
         * Min spin rotation in frames.
         * @property MinSpinRotation
         * @type number
         * @default 40
         */
        if (node.hasAttribute(RouletteWheelConfigurationAttributes.MIN_SPIN_ROTATION_LONG)) {
            this._minSpinRotation = parseInt(
                node.getAttribute(RouletteWheelConfigurationAttributes.MIN_SPIN_ROTATION_LONG),
            );
        } else {
            this._minSpinRotation = 40;
        }

        /*
         * Min spin rotation in frames for outro high wins.
         * @property MinSpinRotationLong
         * @type number
         * @default 100
         */
        if (node.hasAttribute(RouletteWheelConfigurationAttributes.MIN_SPIN_ROTATION_LONG)) {
            this._minSpinRotationLong = parseInt(
                node.getAttribute(RouletteWheelConfigurationAttributes.MIN_SPIN_ROTATION_LONG),
            );
        } else {
            this._minSpinRotationLong = 100;
        }
    }

    /**
     * Start spinning reels.
     * @method spinStart
     * @param longRotation boolean
     * @return void
     */
    public spinStart(longRotation?: boolean): void {
        if (DEBUG) {
            this.log("spinStart");
        }

        this._stoppedFromServer = false;
        this._stoppedFromMachine = false;
        this._stoppedFromLegal = false;

        this._startSpinTime = Date.now();

        if (this._ball != null) {
            this._ball.spinStart(this._spinContainer.rotation);

            if (longRotation != null && longRotation) {
                this._stopTimer.start(this._minSpinRotationLong);
            } else {
                this._stopTimer.start(this._minSpinRotation);
            }
        }
    }

    protected verifyMinGameRoundDurationConditions(): boolean {
        return (
            AppContext.gameConfiguration.MinGameRoundDuration > 0 || AppContext.gameConfiguration.GameRoundMinSpan > 0
        );
    }

    /**
     * Stop reels rotation.
     * @method spinStop
     * @param event any - from timer
     * @param force boolean - default false used when spin_stop was dispatch
     * @param fromServer boolean - default false used when spin_stop was dispatch from server
     * @param legal
     * @return void
     */
    public spinStop(force: boolean = false, fromServer: boolean = false, legal: boolean = false): void {
        if (fromServer) {
            this._stoppedFromServer = true;
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            if (this.verifyMinGameRoundDurationConditions()) {
                this._legalStopTimer = setTimeout(
                    () => this.spinStop(false, false, true),
                    this.getLegalRemainingSpinDuration(),
                );
            } else {
                this._stoppedFromLegal = true;
            }
        } else if (legal) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
        } else {
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (force) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (this._stoppedFromServer && this._stoppedFromMachine && this._stoppedFromLegal) {
            if (this._ball != null) {
                if (GameManager.rouletteResponse != null && GameManager.rouletteResponse.chosenNumber != null) {
                    this._chosenNumber = GameManager.rouletteResponse.chosenNumber;
                    let finalAngle = Wheel.NumberOrder.indexOf(this._chosenNumber) * (360 / Wheel.NumberOrder.length);

                    // console.log("%c StopNumber: " + this._chosenNumber, "font-size: 24px; color: #BADA55");
                    this._ball.spinStop(finalAngle, force);
                }
            }
        }
    }

    /**
     * Get remaining time for legal spin duration
     * @method getLegalRemainingSpinDuration
     * @return number
     */
    protected getLegalRemainingSpinDuration(): number {
        if (AppContext.gameConfiguration.GameRoundMinSpan > 0) {
            return this.calculateRemainingSpinDuration(AppContext.gameConfiguration.GameRoundMinSpan);
        }

        if (AppContext.gameConfiguration.MinGameRoundDuration > 0) {
            return AppContext.gameConfiguration.MinGameRoundDuration;
        }

        return 0;
    }

    /**
     * Calculate remaining time for legal spin duration
     * @method calculateRemainingSpinDuration
     * @param minTime: number - minimal spin duration in seconds
     * @return number
     */
    protected calculateRemainingSpinDuration(minTime: number): number {
        const now = Date.now();
        const diff = now - this._startSpinTime;
        const reelStopDuration = this.getWheelStopDuration();
        return Math.max(minTime - diff - reelStopDuration, 0);
    }

    //150f win number,
    protected getWheelStopDuration(): number {
        return ((150 + this._ball.getStopDuration()) / 60) * 1000;
    }

    private wheelStopped(): void {
        //due to quickspin dialog that pause game, wheel needs to be postponed
        this._wheelStopTimer.start(1);
    }

    private emitWheelStopped(): void {
        this.emit(Wheel.WheelStopped);

        if (DEBUG) {
            const old = AppContext.gameConfiguration.MinGameRoundDuration;
            const current = AppContext.gameConfiguration.GameRoundMinSpan;
            const minDuration = current > 0 ? current - (150 / 60) * 1000 : old > 0 ? old - (150 / 60) * 1000 : 0;
            const spinDuration = Date.now() - this._startSpinTime;

            if (spinDuration > minDuration || !this.verifyMinGameRoundDurationConditions()) {
                this.log("Spin time duration: " + spinDuration + " ms");
            } else {
                console.log("%cError spin time duration:" + spinDuration + " ms", "color: #ff9900");
            }
        }
    }

    public getBallWorldTransform(): PIXI.Matrix {
        return this._ball.getWorldTransform();
    }

    public getBallGlobalPosition(): PIXI.Point {
        return this._ball.getBallGlobalPosition();
    }

    public getBallRotation(): number {
        return this._ball.getBallRotation();
    }

    public getCurrentRotation(): number {
        return this.radians2degrees(this._spinContainer.rotation);
    }

    public getFinalAngle(): number {
        return Wheel.NumberOrder.indexOf(this._chosenNumber) * (360 / Wheel.NumberOrder.length);
    }
}

/**
 * Can inherit attributes from BaseElement
 */
export const WheelAttributes = {
    /*
     * Name of config for machine.
     * @property Configs
     * @type string
     * @default null
     */
    CONFIGS: "Configs",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Wheel.ts
 **/